import { Link } from 'react-router';
import { api } from '../../api/client.js';
import { DataTable, EmptyState, ErrorState, Spinner } from '../../components/ui.jsx';
import { ClaimReceipt } from '../../components/ClaimReceipt.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { formatDate, formatXaf } from '../../lib/format.js';

const monthName = (iso) =>
  new Date(`${iso.slice(0, 7)}-01T12:00:00Z`).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  });
// window.prompt is deliberate here: turning a payment down needs one sentence
// and the worklist is where it is done in passing. The account page has a
// proper dialog for the same thing.

/**
 * The jobs the system cannot do itself. Cutting a wire and reconnecting it are
 * things a person has to go and do, so the software's part is to say who, why,
 * and to stop asking once somebody says it is done.
 */
const UtilityTasks = () => {
  useTitle('Utility worklist');
  const toast = useToast();
  const tasks = useFetch('/landlord/utility-tasks');

  if (tasks.loading && !tasks.data) return <Spinner />;
  if (tasks.error) {
    return (
      <div className="container section">
        <ErrorState message={tasks.error} onRetry={tasks.reload} />
      </div>
    );
  }

  const { claims = [], disconnects, reconnects, readingRounds = [] } = tasks.data;

  const reviewClaim = async (claim, action) => {
    const reason =
      action === 'reject'
        ? window.prompt('What should they be told? This is sent to them and kept on the record.')
        : null;
    if (action === 'reject' && !reason) return;

    try {
      await api.post(`/landlord/utility-claims/${claim.id}/${action}`, reason ? { reason } : undefined);
      toast.success(action === 'confirm' ? 'Confirmed. The receipt has been emailed over.' : 'Turned down.');
      tasks.reload();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const claimColumns = [
    {
      key: 'who',
      header: 'Customer',
      render: (claim) => (
        <div className="cell-stack">
          <Link to={`/landlord/utilities/accounts/${claim.account?.id}`} className="cell-title">
            {claim.claimedByName}
          </Link>
          <span className="muted">{claim.service?.name}</span>
        </div>
      )
    },
    {
      key: 'says',
      header: 'They say they sent',
      render: (claim) => (
        <div className="cell-stack">
          <strong>{formatXaf(claim.amount)}</strong>
          <span className="muted">
            {claim.method.replace(/_/g, ' ')}
            {claim.reference ? ` - ${claim.reference}` : ''} on {formatDate(claim.paidOn)}
          </span>
          {claim.note ? <span className="muted">"{claim.note}"</span> : null}
          <ClaimReceipt url={claim.receiptUrl} />
        </div>
      )
    },
    {
      key: 'bill',
      header: 'Against',
      render: (claim) => (
        <div className="cell-stack">
          <span>{claim.bill?.reference}</span>
          <span className="muted">{formatXaf(claim.bill?.amount)} billed</span>
        </div>
      )
    },
    {
      key: 'actions',
      header: '',
      render: (claim) => (
        <div className="cell-actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={() => reviewClaim(claim, 'confirm')}>
            Confirm
          </button>
          <button type="button" className="link-btn link-btn-danger" onClick={() => reviewClaim(claim, 'reject')}>
            Not received
          </button>
        </div>
      )
    }
  ];

  const confirm = async (account, what) => {
    try {
      await api.post(`/landlord/utility-accounts/${account.id}/confirm-${what}`);
      toast.success(what === 'disconnection' ? 'Marked as cut off.' : 'Marked as back on.');
      tasks.reload();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const columns = (what) => [
    {
      key: 'who',
      header: 'Customer',
      render: (account) => (
        <div className="cell-stack">
          <Link to={`/landlord/utilities/accounts/${account.id}`} className="cell-title">
            {account.customer?.name}
          </Link>
          {account.customer?.phone ? (
            <a className="muted" href={`tel:${account.customer.phone}`}>
              {account.customer.phone}
            </a>
          ) : null}
        </div>
      )
    },
    {
      key: 'what',
      header: 'Service',
      render: (account) => (
        <div className="cell-stack">
          <span>{account.service?.name}</span>
          <span className="muted">{account.meterNumber ? `meter ${account.meterNumber}` : account.label}</span>
        </div>
      )
    },
    {
      key: 'why',
      header: what === 'disconnection' ? 'Unpaid' : 'Settled',
      render: (account) =>
        what === 'disconnection' ? (
          <div className="cell-stack">
            <strong className="owing">{formatXaf(account.balance?.balance)}</strong>
            <span className="muted">cut off {formatDate(account.cutOffAt)}</span>
          </div>
        ) : (
          <span className="muted">since {formatDate(account.reconnectDueAt)}</span>
        )
    },
    {
      key: 'actions',
      header: '',
      render: (account) => (
        <div className="cell-actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={() => confirm(account, what)}>
            {what === 'disconnection' ? 'I have cut it off' : 'I have put it back on'}
          </button>
          {account.customer?.phone ? (
            <a className="btn btn-ghost btn-sm" href={`tel:${account.customer.phone}`}>
              Call
            </a>
          ) : null}
        </div>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Utility worklist</h1>
          <p className="muted">Who needs cutting off, and who has paid and is waiting to be put back on.</p>
        </div>
        <div className="row-actions">
          <Link to="/landlord/utilities" className="btn btn-ghost">
            All services
          </Link>
        </div>
      </header>

      {claims.length === 0 && disconnects.length === 0 && reconnects.length === 0 && readingRounds.length === 0 ? (
        <EmptyState
          title="Nothing to do"
          message="Every meter read for the month, no payments waiting to be confirmed, nobody past their deadline, nobody waiting to be reconnected."
        />
      ) : null}

      {/* Nothing can read a dial on a wall, so the month's round is the one job
          the app can only ever hand to a person. It opens itself, counts what is
          left, and goes away when the last meter is in. */}
      {readingRounds.length > 0 ? (
        <section className="section-block">
          <div className="section-head">
            <h2>Meters to read</h2>
            <span className="muted">
              Walk the meters and enter the figures. The arithmetic and the bills are done for you.
            </span>
          </div>
          <DataTable
            columns={[
              {
                key: 'month',
                header: 'Month',
                render: (round) => (
                  <div className="cell-stack">
                    <span className="cell-title">{monthName(round.periodStart)}</span>
                    <span className="muted">{round.serviceName}</span>
                  </div>
                )
              },
              {
                key: 'left',
                header: 'Still to read',
                render: (round) => (
                  <div className="cell-stack">
                    <strong>
                      {round.stillToRead} of {round.meters}
                    </strong>
                    {round.readSoFar > 0 ? (
                      <span className="muted">{round.readSoFar} already done</span>
                    ) : null}
                  </div>
                )
              },
              {
                key: 'opened',
                header: 'Due since',
                render: (round) => <span className="muted">{formatDate(round.openedOn)}</span>
              },
              {
                key: 'go',
                header: '',
                render: (round) => (
                  <Link
                    to={`/landlord/utilities/${round.serviceId}/monthly?month=${round.periodStart.slice(0, 7)}`}
                    className="btn btn-primary btn-sm"
                  >
                    Read them
                  </Link>
                )
              }
            ]}
            rows={readingRounds}
            rowKey={(round) => round.id}
          />
        </section>
      ) : null}

      {claims.length > 0 ? (
        <section className="section-block">
          <div className="section-head">
            <h2>Payments to confirm</h2>
            <span className="muted">
              Money customers say they have sent. None of it counts until you confirm it arrived.
            </span>
          </div>
          <DataTable columns={claimColumns} rows={claims} rowKey={(row) => row.id} />
        </section>
      ) : null}

      {disconnects.length > 0 ? (
        <section className="section-block">
          <div className="section-head">
            <h2>Cut off</h2>
            <span className="muted">Two days past the deadline with the bill still unpaid.</span>
          </div>
          <DataTable columns={columns('disconnection')} rows={disconnects} rowKey={(row) => row.id} />
        </section>
      ) : null}

      {reconnects.length > 0 ? (
        <section className="section-block">
          <div className="section-head">
            <h2>Put back on</h2>
            <span className="muted">They have paid. The service is marked on already; the wire is not.</span>
          </div>
          <DataTable columns={columns('reconnection')} rows={reconnects} rowKey={(row) => row.id} />
        </section>
      ) : null}
    </div>
  );
};

export default UtilityTasks;
